import type { GlobeConfiguration, GlobeKeyframe, GlobeLocation, GlobeRoute, GlobeView } from '@colescomex/globe-runtime';
import type { Locale } from '../i18n/locale';

/**
 * The globe has two jobs on the HOME (docs/phase-0/03):
 *   HERO    — Colombia (node at Cartagena) as the commercial destination, with China and the United
 *             States as the two clear origin corridors and Europe restrained; calm kit traffic.
 *   JOURNEY — later, the same planet is scrubbed by scroll from Asia across the Pacific to the
 *             Caribbean, where the United States corridor joins: origin → Colombia.
 * Corridors are illustrative commercial routes, not navigational or AIS data.
 * Framing is chosen per device profile (phone, tablet portrait, desktop), never just scaled.
 */
export type DeviceProfile = 'phone' | 'tablet' | 'desktop';
export function deviceProfile(width: number, height: number): DeviceProfile {
  if (width < 768) return 'phone';
  if (width < 1200 && height >= width) return 'tablet';
  return 'desktop';
}

const node = (id: string, label: string, lat: number, lng: number, extra: Partial<GlobeLocation> = {}): GlobeLocation =>
  ({ id, label, lat, lng, emphasis: 'normal', labelVisible: true, priority: 60, ...extra });

const LABELS: Record<Locale, Record<'cartagena' | 'china' | 'usa' | 'europe', string>> = {
  'es-CO': { cartagena: 'CARTAGENA, COLOMBIA', china: 'CHINA', usa: 'ESTADOS UNIDOS', europe: 'EUROPA' },
  en: { cartagena: 'CARTAGENA, COLOMBIA', china: 'CHINA', usa: 'UNITED STATES', europe: 'EUROPE' },
};
export function locations(locale: Locale = 'es-CO'): GlobeLocation[] {
  const label = LABELS[locale];
  return [
    node('cartagena', label.cartagena, 10.39, -75.51, { emphasis: 'primary', priority: 100 }),
    node('china', label.china, 22.5, 114.1, { priority: 80 }),
    node('usa', label.usa, 29.8, -95.4, { priority: 80 }),
    node('europe', label.europe, 51.9, 4.5, { priority: 50, emphasis: 'subtle' }),
  ];
}
export const LOCATIONS = locations();

/** Route hierarchy: China and the United States are the two clear origin corridors; Europe is restrained. */
const primary = { color: '#00b4db', fromColor: '#0066b3', headColor: '#ffffff', width: 2.1, opacity: 1, arcHeight: 0.1, maxArcHeight: 0.13,
  particles: { enabled: true, count: 2, speed: 0.16, size: 4.5, tail: 0.16 } };
const origin = { color: '#36c3e3', fromColor: '#0066b3', headColor: '#ffffff', width: 1.8, opacity: 0.92, arcHeight: 0.06, maxArcHeight: 0.09,
  particles: { enabled: true, count: 1, speed: 0.15, size: 4, tail: 0.14 } };
const secondary = { color: '#8ec3e6', fromColor: '#0066b3', headColor: '#eef5f8', width: 1, opacity: 0.42, arcHeight: 0.07, maxArcHeight: 0.1,
  particles: { enabled: false, count: 0, speed: 0.14, size: 3, tail: 0.12 } };
/** Illustrative lower-latitude maritime corridor across the Pacific and Panama (not navigational data). */
export const PACIFIC_CORRIDOR = [
  { lat: 20, lng: 138 }, { lat: 17, lng: 160 }, { lat: 14, lng: 179 },
  { lat: 12, lng: -160 }, { lat: 10, lng: -136 }, { lat: 9, lng: -112 },
  { lat: 8.8, lng: -92 }, { lat: 9, lng: -79.5 }, { lat: 11, lng: -77.5 },
];
export const ROUTES: GlobeRoute[] = [
  { id: 'cn-ctg', from: 'china', to: 'cartagena', waypoints: PACIFIC_CORRIDOR, labelAnchor: 'none', style: primary, active: false },
  { id: 'us-ctg', from: 'usa', to: 'cartagena', labelAnchor: 'none', style: origin, active: false },
  { id: 'eu-ctg', from: 'europe', to: 'cartagena', labelAnchor: 'none', style: secondary, active: false },
];
/** Progressive reveal order at load: primary corridor first, then global reach. */
export const ROUTE_REVEAL = ['cn-ctg', 'us-ctg', 'eu-ctg'];

export const HERO_VIEW: GlobeView = { lat: 14, lng: -58, zoom: 0.95 };

/** Journey keyframes: Asia → the Pacific → the Caribbean, where the United States corridor joins → Colombia. */
const JOURNEY_ROUTES = ['cn-ctg', 'us-ctg'];
export const JOURNEY_FRAMES: GlobeKeyframe[] = [
  { at: 0, view: { lat: 24, lng: 116, zoom: 1.35 }, routes: JOURNEY_ROUTES, scale: 1, opacity: 1 },
  { at: 0.34, view: { lat: 16, lng: 178, zoom: 1.0 }, routes: JOURNEY_ROUTES },
  { at: 0.68, view: { lat: 16, lng: -92, zoom: 1.2 }, routes: JOURNEY_ROUTES },
  { at: 1, view: { lat: 12, lng: -78, zoom: 1.55 }, routes: JOURNEY_ROUTES, scale: 1, opacity: 1 },
];

export const GLOBE_THEME = {
  // Opaque, matched to the page: the engine's additive atmosphere writes alpha = 1, which on a
  // transparent canvas turns its faded outer shell into a black ring.
  background: '#041327',
  globe: { ocean: '#0b2a4f', coldGlow: '#0066b3', atmosphere: '#3f8fc4', sunGlow: '#9cc9e4' },
  dots: { color: '#eef5f8', opacity: 0.86, nightColor: '#1d4a78' },
  routes: { primary: '#00b4db', secondary: '#0066b3', head: '#eef5f8' },
  markers: { color: '#c3d4de', primary: '#00b4db' },
  labels: { color: '#eef5f8', muted: 'rgba(238,245,248,0.62)', font: "600 10px/1.2 'Inter Variable', Inter, sans-serif" },
} as const;

/** Interaction model shared by every profile (the engine owns the AUTO → DRAG → INERTIA → RESUME machine). */
export const GLOBE_INTERACTION = {
  drag: true, touch: false, keyboard: false, inertia: true, zoom: false, touchAction: 'pan-y',
  autoRotate: true, autoRotateSpeed: 0.02, resumeDelay: 2000, resumeDuration: 2400, resumeThreshold: 0.1,
  damping: 0.94, sensitivity: { mouse: 0.75, touch: 0.6 }, minPitch: -55, maxPitch: 55,
} as const;

/** Per-device framing: desktop globe to the right of the copy; tablet portrait and phone above it. */
const FRAMING: Record<DeviceProfile, { fit: number; offset: { x: number; y: number }; dot: number; dpr: number }> = {
  desktop: { fit: 0.9, offset: { x: 0.2, y: -0.01 }, dot: 2, dpr: 2 },
  tablet: { fit: 1.02, offset: { x: 0, y: -0.17 }, dot: 1.9, dpr: 2 },
  phone: { fit: 1.22, offset: { x: 0, y: -0.15 }, dot: 1.6, dpr: 1.5 },
};

const ACCESSIBILITY: Record<Locale, { title: string; description: string }> = {
  'es-CO': {
    title: 'Colombia conectada con China, Estados Unidos y Europa',
    description: 'Globo ilustrativo: corredores comerciales hacia Colombia desde China y Estados Unidos, y un corredor secundario desde Europa, con aviones de carga, buques y camiones en movimiento. No representa operaciones reales.',
  },
  en: {
    title: 'Colombia connected with China, the United States and Europe',
    description: 'Illustrative globe: commercial corridors into Colombia from China and the United States, and a secondary corridor from Europe, with cargo aircraft, vessels and trucks in motion. It does not represent actual operations.',
  },
};

export function globeConfiguration(profile: DeviceProfile, locale: Locale = 'es-CO'): GlobeConfiguration {
  const frame = FRAMING[profile];
  return {
    accessibility: ACCESSIBILITY[locale],
    locations: locations(locale), routes: ROUTES, routeMode: 'inbound',
    camera: { initial: HERO_VIEW, fit: frame.fit, offset: frame.offset },
    // Desktop: slow auto-rotation + weighted drag. Pointer down pauses rotation, release settles with inertia,
    // and after ~2 s of inactivity rotation eases back in from the current orientation (no snap, no reset).
    // Phones/tablets: automatic only — touch never competes with page scrolling. Reduced motion: no auto-rotation.
    interaction: { ...GLOBE_INTERACTION, enabled: profile === 'desktop' },
    appearance: {
      theme: GLOBE_THEME,
      sun: { direction: { x: 0.55, y: 0.45, z: 0.7 }, color: '#eef5f8', intensity: 1, ambient: 0.24 },
      dots: { spacing: 'auto', size: frame.dot }, atmosphere: { enabled: true, thickness: 0.1, intensity: 0.55 },
      clouds: { enabled: false }, stars: { enabled: false },
    },
    labels: { enabled: true, max: 4, showRouteLabels: false },
    performance: { maxDpr: frame.dpr, adaptiveDpr: true, targetFps: 60, antialias: profile !== 'phone' },
  };
}
